import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { useTheme } from '../../../../core/ui/ThemeContext';
import { useLanguage } from '../../../../core/i18n/LanguageContext';
import { getGlassStyles } from './ProductManagementStyles';

// Strukturierter Merkmale-Editor fuer interne Produkte (product_attributes-Kindtabelle).
// Verwaltet ein attributes[]-Array mit
//   { label, value, unit, char_name, char_group, sort_order }.
// Sendet Aenderungen via onAttributesChange(newArray).
// disabled=true fuer SAP-Quellen (schreibgeschuetzt).
const AttributesEditor = ({ attributes, onAttributesChange, disabled = false }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const styles = getGlassStyles(isDarkMode);

  const addRow = () => {
    const nextOrder = attributes.length > 0
      ? Math.max(...attributes.map((a) => a.sort_order ?? 0)) + 10
      : 10;
    onAttributesChange([
      ...attributes,
      { char_name: '', label: '', value: '', unit: '', char_group: '', sort_order: nextOrder },
    ]);
  };

  const removeRow = (idx) => {
    onAttributesChange(attributes.filter((_, i) => i !== idx));
  };

  const updateRow = (idx, field, value) => {
    onAttributesChange(
      attributes.map((a, i) => (i !== idx ? a : { ...a, [field]: value }))
    );
  };

  const inputSm = `${styles.inputClassName} py-2 text-sm`;

  return (
    <div className="space-y-3">
      {Array.isArray(attributes) && attributes.length > 0 ? (
        <>
          {/* Tabellen-Kopf */}
          <div className={`grid grid-cols-12 gap-2 px-1 text-xs font-semibold uppercase tracking-wide ${isDarkMode ? 'text-white/50' : 'text-slate-500'}`}>
            <span className="col-span-3">{t('admin.productForm.attributes.label')}</span>
            <span className="col-span-3">{t('admin.productForm.attributes.value')}</span>
            <span className="col-span-2">{t('admin.productForm.attributes.unit')}</span>
            <span className="col-span-3">{t('admin.productForm.attributes.charName')}</span>
            <span className="col-span-1"></span>
          </div>

          {attributes.map((row, idx) => (
            <div key={idx} className="grid grid-cols-12 gap-2 items-center">
              {/* Bezeichnung (label) */}
              <div className="col-span-3 relative group/input">
                <div className={styles.inputGlassBackground}></div>
                <input
                  type="text"
                  value={row.label}
                  onChange={(e) => updateRow(idx, 'label', e.target.value)}
                  disabled={disabled}
                  placeholder={t('admin.productForm.attributes.label')}
                  className={`${inputSm} ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
                />
                <div className={styles.focusHighlight}></div>
              </div>

              {/* Wert (value) */}
              <div className="col-span-3 relative group/input">
                <div className={styles.inputGlassBackground}></div>
                <input
                  type="text"
                  value={row.value}
                  onChange={(e) => updateRow(idx, 'value', e.target.value)}
                  disabled={disabled}
                  placeholder={t('admin.productForm.attributes.value')}
                  className={`${inputSm} ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
                />
                <div className={styles.focusHighlight}></div>
              </div>

              {/* Einheit (unit) */}
              <div className="col-span-2 relative group/input">
                <div className={styles.inputGlassBackground}></div>
                <input
                  type="text"
                  value={row.unit}
                  onChange={(e) => updateRow(idx, 'unit', e.target.value)}
                  disabled={disabled}
                  placeholder={t('admin.productForm.attributes.unit')}
                  className={`${inputSm} ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
                />
                <div className={styles.focusHighlight}></div>
              </div>

              {/* Technischer Name (char_name) */}
              <div className="col-span-3 relative group/input">
                <div className={styles.inputGlassBackground}></div>
                <input
                  type="text"
                  value={row.char_name}
                  onChange={(e) => updateRow(idx, 'char_name', e.target.value.toUpperCase())}
                  disabled={disabled}
                  placeholder={t('admin.productForm.attributes.charName')}
                  className={`${inputSm} font-mono text-xs ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
                />
                <div className={styles.focusHighlight}></div>
              </div>

              {/* Zeile loeschen */}
              <div className="col-span-1 flex justify-center">
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => removeRow(idx)}
                    title={t('admin.productForm.attributes.removeRow')}
                    className={`p-1.5 rounded-lg transition-colors ${
                      isDarkMode
                        ? 'text-red-400/70 hover:text-red-400 hover:bg-red-500/10'
                        : 'text-red-500/70 hover:text-red-600 hover:bg-red-50'
                    }`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </>
      ) : (
        <p className={`text-sm ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>
          {t('admin.productForm.attributes.empty')}
        </p>
      )}

      {/* Zeile hinzufuegen */}
      {!disabled && (
        <button
          type="button"
          onClick={addRow}
          className={`flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-xl border transition-colors ${
            isDarkMode
              ? 'border-white/20 text-white/70 hover:text-white hover:border-white/40 hover:bg-white/5'
              : 'border-slate-200 text-slate-600 hover:text-brand hover:border-brand/40 hover:bg-brand/5'
          }`}
        >
          <Plus className="w-4 h-4" />
          {t('admin.productForm.attributes.addRow')}
        </button>
      )}
    </div>
  );
};

export default AttributesEditor;
